<template>
  <div 
    class="search" 
    :class="[
        {'is-filled': filled}, 
        {'is-results-open': open},
        {'is-side-space': sideSpace},
        {'is-mobile-disabled': disableOnMobile && mobile},
        {'is-standalone': standalone},
        {'is-error': error},
        {'is-in-panel': inPanel},
    ]" 
    @keyup="elKeyupHandler" 
    v-click-outside="clickOutsideHandler"
>
        <!-- <div class="search__field"> -->
        <div class="search__field" :class="{'is-disabled' : disabled && interimMessageTabs.indexOf(activeTransportModeTab) > -1}" >
            <Loader class="search__field__loader" v-if="loading"/>
            <label class="search__field__number" v-else-if="fieldIcon.showIndex">
                {{ fieldIcon.index }}
            </label>
            <label v-else :for="`search-field-${fieldIndex}`" class="search__field__icon">
                <icon>
                    <component :is="fieldIcon"/>
                </icon>
            </label>
            <input 
                class="search__field__input" 
                type="text" 
                :id="`search-field-${fieldIndex}`"
                ref="search-field"
                :placeholder="placeholder" 
                autocomplete="off"
                :value="searchText"
                :disabled="disabled && interimMessageTabs.indexOf(activeTransportModeTab) > -1"
                @input="inputInputHandler"
                @focus="inputFocusHandler"
                @blur="inputBlurHandler"
                @paste="inputPasteHandler"
            />
            <div class="search__field__actions">
                <div class="search__field__clear search__field__action" @click="clearClickHandler">
                    <icon view-box="0 0 21 21" :width="21" :height="21">
                        <IconCloseCircle/>
                    </icon>
                </div>
                <div class="search__field__drag-handle search__field__action" v-if="interimMessageTabs.indexOf(activeTransportModeTab) === -1 || (interimMessageTabs.indexOf(activeTransportModeTab) > -1 && move)">
                    <SmrTooltip content="Ziehen, um Reihenfolge zu ändern" position="bottom">
                        <slot name="drag-handle" />
                    </SmrTooltip>  
                </div>
            </div>
        </div>

        <div class="search__results" @mouseleave="resultsMouseLeaveHandler">
            <div class="search__results__section" v-for="(resultsSection, sectionIndex) in results" :key="sectionIndex">
                <div class="search__results__section__label" v-if="resultsSection.label && lastSearches.length || resultsSection.label && lastRoutes.length">{{ resultsSection.label }}</div>
                <div 
                    class="search__result" 
                    :class="[
                        {'search__result--spacious': resultsSection.spacious}, 
                        {'search__result--selected': result.selected},
                        {'search__result--named': resultsSection.named},
                        {'search__result--bold-name': resultsSection.boldName}, 
                    ]" 
                    v-for="(result, resultIndex) in resultsSection.items" 
                    v-bind:key="result.id + (result.preset ? '-preset' : result.name + result.comment )"
                    @mouseenter="resultMouseEnterHandler({sectionIndex, resultIndex})"
                    @click="resultClickHandler({sectionIndex, resultIndex}, result.type, resultsSection.label)"
                >
                    <div class="search__result__inner" :class="{'search__result__inner--spacing-big': getBiggerIcon(result.type)}">
                        <img class="search__result__image" :src="getImageForLocationCategory(result.type)" v-if="result.visualType && result.visualType === 'image'"/>
                        <img class="search__result__image search__result__image--big" :src="result.preparedPoiData.image" v-else-if="result.preparedPoiData && result.preparedPoiData.image"/>
                        <!--Bigger icon for work, home, favourites -->
                        <icon class="search__result__icon search__result__icon--big" :width="mobile ? '38' : '40'" :height="mobile ? '38' : '40'" :view-box=" mobile ? '0 0 38 38' : '0 0 40 40'" v-else-if="getBiggerIcon(result.type)"> 
                            <component :is="getIconForLocationCategory(result.type)"/>
                        </icon>
                        <icon class="search__result__icon" v-else>
                            <component :is="getIconForLocationCategory(result.type)"/>
                        </icon>
                        <div class="search__result__texts">
                            <span class="search__result__texts__name" v-html="result.highlight"></span>
                            <span class="search__result__texts__address" v-if="result.comment">
                                {{ result.comment }}
                            </span>
                        </div>  
                        <div class="search__result__link-icon" v-if="result.link && !showRemoveIcon({sectionIndex}, result.type)">
                            <icon :width="20" :height="20"><IconChevronRight/></icon>
                        </div>
                        <div class="search__result__link-icon search__result__link-icon--close" v-if="showRemoveIcon({sectionIndex}, result.type)" @click="removeClickHandler({resultIndex, result}, $event)">
                            <icon :width="20" :height="20"><IconClose/></icon>
                        </div>
                        <div class="search__result__arrow" @click="shortcutArrowClickHandler($event, {sectionIndex, resultIndex})" v-if="!result.preset && !result.link && mobile && result.type !== 'LOCATION'">
                            <icon :width="20" :height="20"><IconArrowShortcutCircle/></icon>
                        </div>
                    </div>
                </div>
            </div>
         
        </div>
  </div>
</template>

<script>
import eventBus from '@/utils/event-bus';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import ClickOutside from 'vue-click-outside'
import Loader from '@/components/Loader.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
import Icon from '@/components/Icon.vue'
import IconSearchMarkStart from '@/components/icons/IconSearchMarkStart.vue'
import IconSearchMarkTarget from '@/components/icons/IconSearchMarkTarget.vue'
import IconSearchMarkInterim from '@/components/icons/IconSearchMarkInterim.vue'
import IconSearch from '@/components/icons/IconSearch.vue'
import IconPin from '@/components/icons/IconPin.vue'
import IconLocation from '@/components/icons/IconLocation.vue'
import IconStreet from '@/components/icons/IconStreet.vue'
import IconPlane from '@/components/icons/IconPlane.vue'
import IconBorder from '@/components/icons/IconBorder.vue'
import IconBus from '@/components/icons/IconBus.vue'
import IconPinBig from '@/components/icons/IconPinBig.vue'
import IconOEAMTC from '@/components/icons/IconOEAMTC.vue'
import IconParking from '@/components/icons/IconParking.vue'
import IconMountainRoad from '@/components/icons/IconMountainRoad.vue'
import IconReststop from '@/components/icons/IconReststop.vue'
import IconShip from '@/components/icons/IconShip.vue'
import IconTrain from '@/components/icons/IconTrain.vue'
import IconTunnel from '@/components/icons/IconTunnel.vue'
import IconCloseCircle from '@/components/icons/IconCloseCircle.vue'
import IconClose from '@/components/icons/IconClose.vue'
import IconArrowShortcutCircle from '@/components/icons/IconArrowShortcutCircle.vue'
import IconHouse from '@/components/icons/IconHouse.vue'
import IconSuitcase from '@/components/icons/IconSuitcase.vue'
import IconStarOutlineYellow from '@/components/icons/IconStarOutlineYellow.vue'
import IconChevronRight from '@/components/icons/IconChevronRight.vue'
import IconLocateOutline from '@/components/icons/IconLocateOutline.vue'
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import { searchTracking } from '@/data/trackingInfo';
import { ga4SearchTracking } from '@/data/trackingInfo';
import * as api from '@/api';

export default {
    directives: {
        ClickOutside
    },
    components: {
        Loader,
        Icon,
        IconSearchMarkStart,
        IconSearchMarkTarget,
        IconSearchMarkInterim,
        IconSearch,
        IconPin,
        IconLocation,
        IconStreet,
        IconPlane,
        IconBorder,
        IconBus,
        IconPinBig,
        IconOEAMTC,
        IconParking,
        IconMountainRoad,
        IconReststop,
        IconShip,
        IconTrain,
        IconTunnel,
        IconCloseCircle,
        IconClose,
        IconArrowShortcutCircle,
        IconHouse,
        IconSuitcase,
        IconStarOutlineYellow,
        IconChevronRight,
        IconLocateOutline,
        SmrTooltip,
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        fieldIcon: {
            type: [String, Object],
            default: ''
        },
        fieldIndex: {
            type: Number,
            default: 0
        },
        sideSpace: {
            type: Boolean,
            default: false
        },
        disableOnMobile: {
            type: Boolean,
            default: false
        },
        standalone: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        locationSuggestionsOnly: {
            type: Boolean,
            default: false
        },
        inPanel: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        move: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return  {
            searchText: '',
            filled: false,
            focused: false,
            open: false,
            loading: false,
            results: [],
            selectedSectionIndex: 0,
            selectedResultIndex: 0,
            defaultStateShown: true,
            disableAutosearch: false,
            blurSubmitCheck: false,
            resultSubmitted: false,
            autoSubmitResult: false,
            loadSearchDataTimer: null,
            coordinates: [],
            currentLocationLoading: false,
            interimMessageTabs: ['PUBLIC', 'BIKE_AND_RIDE', 'PARK_AND_RIDE', 'SHARING'], // show interim stop message for these tabs
            COORDINATE_REGEX:  /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/
        }
    },
    watch: {
        value(v) {
            if(v.includes('Aktuelle Position, undefined' || v.includes('Aktuelle Position'))) {
                this.searchText = '';
            } 
            else this.searchText = v;
        },
        coordinates(v) {
            this.coordinates = v;
        },
    
        defaultState(v) {
            return v;
        },
        currentLocationLoading(v) {
            return this.currentLocationLoading = v;
        },
        searchText: function(value, oldValue) {
            if(this.focused || value === '') {
                this.resultSubmitted = false;
            }
            if(value.includes('Aktuelle Position, undefined') || value.includes('Aktuelle Position')) this.searchText = '';
            if(this.COORDINATE_REGEX.test(value.trim())) {
                if(value !== oldValue) this.resultSubmitted = false;
                this.debouncedCoordinateSearch()
            } else if(!this.disableAutosearch) {
                // check if old value are coordinates and reset input accordingly
                if(this.focused && (this.COORDINATE_REGEX.test(oldValue.trim()) || !this.open)) {
                    this.openResults()
                } else {
                    this.loadDropdownContent();
                }
            }
            this.filled = value.length;
        },
        mapFullyLoaded() {
            if(this.autoSubmitResult) {
                setTimeout(() => {
                    this.submitResult() 
                    this.autoSubmitResult = false
                }, 10);
            }
        },
        user() {
            // Populate Home / Work address in relevant search field after logging in, otherwise show dialog
            if (this.user && this.openFavouriteOverlayAfterLogin.fieldIndex === this.fieldIndex) {
                if (this.openFavouriteOverlayAfterLogin.specialPreset === 'home') {
                    setTimeout(() => { // Wait for the response from server
                        if (this.favouriteHome) {
                            this.searchText = this.favouriteHome.name
                            this.loadAndSubmit()
                        } else {
                            eventBus.$emit('showFavouritesPanel', this.openFavouriteOverlayAfterLogin)
                        }
                        this.$store.commit('setOpenFavouriteOverlayAfterLogin', {})
                    }, 300)
                } else if (this.openFavouriteOverlayAfterLogin.specialPreset === 'work') {
                    setTimeout(() => {
                        if (this.favouriteWork) {
                            this.searchText = this.favouriteWork.name
                            this.loadAndSubmit()
                        } else {
                            eventBus.$emit('showFavouritesPanel', this.openFavouriteOverlayAfterLogin)
                        }
                        this.$store.commit('setOpenFavouriteOverlayAfterLogin', {})
                    }, 300)
                }
            }
        },
    },
    computed: {
        ...mapGetters(['mobile', 'defaultFavourites', 'lastSearches', 'lastRoutes', 'user', 'favouriteHome', 'favouriteWork', 'mapFullyLoaded', 'activeTransportModeTab', 'openFavouriteOverlayAfterLogin', 'userLocationAddress']),
        defaultState() {
            let defaultState = [];

            // show only location suggestions
            if(this.locationSuggestionsOnly) return defaultState
            let currentLocationItem = {
                type: 'LOCATION',
                highlight: 'Aktuelle Position',
                id: 'location',
                present: true,
                boldName: true,
                name: 'Aktuelle Position',
            }

            let homeItem = {
                isFavourite: true,
                type: 'HOME',
                highlight: 'Zuhause',
                name: 'Zuhause',
                comment: 'Nicht festgelegt',
                id: 'home',
                preset: true,
                presetType: 'favourite'
            }
        
            let workItem = {
                isFavourite: true,
                type: 'WORK',
                highlight: 'Arbeit',
                name: 'Arbeit',
                comment: 'Nicht festgelegt',
                id: 'work',
                preset: true,
                presetType: 'favourite'
            }

            let favouriteTeaser = {
                type: 'FAVOURITES',
                highlight: 'Weitere Favoriten',
                comment: `0 Orte`,
                link: 'favourite-list',
            }
            // let userItems = [currentLocationItem, homeItem, workItem, favouriteTeaser];
            let userItems;
            if(!this.currentLocationLoading) {
                userItems = [currentLocationItem, homeItem, workItem, favouriteTeaser];
            } else {
               userItems = [homeItem, workItem, favouriteTeaser];
            }
            if (this.userLocationAddress.lat && this.userLocationAddress.lon) {
                currentLocationItem.lat = this.userLocationAddress.lat;
                currentLocationItem.lon = this.userLocationAddress.lon;
                currentLocationItem.label = `${this.userLocationAddress.name}, ${this.userLocationAddress.comment}`;
                currentLocationItem.name = this.userLocationAddress.name;
                currentLocationItem.content = this.userLocationAddress.comment;
                currentLocationItem.presetType = false;
            }

            // add user favourites
            if(this.user) { 
                if(this.favouriteHome) {
                    homeItem.lat = this.favouriteHome.lat;
                    homeItem.lon = this.favouriteHome.lon;
                    homeItem.comment = this.favouriteHome.title;
                    homeItem.presetType = false
                }
                if(this.favouriteWork) {
                    workItem.lat = this.favouriteWork.lat;
                    workItem.lon = this.favouriteWork.lon;
                    workItem.comment = this.favouriteWork.title;
                    workItem.presetType = false
                }
                if(this.defaultFavourites.length) { 
                    favouriteTeaser.comment = `${this.defaultFavourites.length} Orte`
                }
            }

            let userItemsSection = {
                boldName: true,
                named: true,
                items: userItems
            }
            defaultState.push(userItemsSection);

            // add last searches
            if(this.lastSearches.length) {
                let lastSearchesObject = {
                    label: 'Zuletzt gesucht',
                    spacious: true,
                    boldName: true,
                    items: this.lastSearches
                }
                defaultState.push(lastSearchesObject);
            }

            // add last routes
            if(this.lastRoutes.length) {
                let lastRoutesObject = {
                    label: 'Zuletzt gesuchte Routen',
                    named: true,
                    boldName: true,
                    items: this.getLastRoutesItems()
                }
                defaultState.push(lastRoutesObject);
            }

            return defaultState;
        },
    },
    mounted() {
        this.searchText = this.value;
        this.resultSubmitted = this.value.length > 0;
        this.addListeners()
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        showRemoveIcon({sectionIndex}/* , type */) {
            // let types = ['HOME', 'WORK', 'FAVOURITES', 'LOCATION'];
            return sectionIndex !== 0
        },
        // Event handlers
        clearClickHandler() {
            this.clearField()
            this.focusOnInput()
        },
        clearSearchFieldsHandler() {
            this.clearField()
        },
        elKeyupHandler(e) {
            this.disableAutosearch = false
            if((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !this.resultSubmitted){ // For moving through the search results
                this.disableAutosearch = true
                this.selectNeighbourResult(e.key === 'ArrowUp' ? 'prev' : 'next');
            } else if (e.key === 'Enter') {
                e.preventDefault();
                e.target.blur()
                this.loadAndSubmit()
            } else if (e.key === 'Tab') { // For submitting and moving to next search input
                this.loadAndSubmit()
                this.closeResults()
                this.$emit('keyup')
            }
        },
        async loadAndSubmit() {
            if(!this.searchText) return false
            if(this.resultSubmitted) return false
            await this.loadSearchData()
            this.submitResult();
        },
        resultMouseEnterHandler({sectionIndex, resultIndex}) {
            this.selectResult(sectionIndex, resultIndex)
        },
        resultClickHandler({sectionIndex, resultIndex}, type, label) {
            this.$store.commit('setSearchInputType', this.placeholder);  
            this.selectResult(sectionIndex, resultIndex)
            this.submitResult();
            let data = false;
            let kind = type;
            if(kind === 'LOCATION') {
                if(!this.userLocationAddress) eventBus.$emit('showLocationPermissionMessage', 'location');
            }
            if(kind === 'WORK') data = this.favouriteWork
            if(kind === 'HOME') data = this.favouriteHome 
            if(label === 'Zuletzt gesucht') kind = 'HISTORY'; 
            if(kind !== 'WORK' && kind !== 'HOME' && kind !== 'HISTORY' && kind !== 'ROUTE' && kind !== 'FAVOURITES' && kind !== 'LOCATION') {
                kind = 'SEARCH'
                trackEvent({ category: 'Route Planner', action: searchTracking(kind, this.placeholder, data).action, label: searchTracking(kind, this.placeholder, data).label }) 
                
                trackGa4Event({ga4Parameter1Name: ga4SearchTracking(kind, this.placeholder, data).name, ga4Parameter1Value: ga4SearchTracking(kind, this.placeholder, data).value, ga4Event: 'route_planner'})
            }
            // if(this.placeholder === 'Zwischenziel' && type !== 'ROUTE' || type === 'FAVOURITES') return;
            // if(kind === 'FAVOURITES') return;
            else {
                trackEvent({ category: 'Route Planner', action: searchTracking(kind, this.placeholder, data).action, label: searchTracking(kind, this.placeholder, data).label }) 
                
                trackGa4Event({ga4Parameter1Name: ga4SearchTracking(kind, this.placeholder, data).name, ga4Parameter1Value: ga4SearchTracking(kind, this.placeholder, data).value, ga4Event: 'route_planner'})
            }  
        },  
        resultsMouseLeaveHandler() {
            this.deselectCurrentResult()
        },
        removeClickHandler({resultIndex, result}, e) {
            e.stopPropagation();
            if(result.type === 'ROUTE') {
                this.lastRoutes.splice(resultIndex, 1);
                this.$store.dispatch('removeLastRoute', this.lastRoutes);
                this.results = this.defaultState;
            } else {
                this.lastSearches.splice(resultIndex, 1);
                this.$store.dispatch('removeLastSearch', this.lastSearches);
                this.results = this.defaultState;
            } 
        },
        inputInputHandler(e) {
            this.searchText = e.target.value;
        },
        inputFocusHandler() {
            if(this.COORDINATE_REGEX.test(this.searchText.trim())) {
                this.focused = true; 
                return false
            }
            if(this.disableOnMobile && this.mobile) return false;
            this.openResults();
            this.focused = true;
        },
        inputBlurHandler() {
            this.blurSubmitCheck = true;
            this.focused = false;
            setTimeout(() => {
                if(this.blurSubmitCheck) { // the search was not submitted by another action so submitting it here
                    if(this.defaultStateShown) return false;
                    if(
                        this.selectedResultIndex < 0 && 
                        this.results.length && 
                        this.results[0].items.length
                    ) { // if avialable select the first result from the list
                        this.selectResult(0, 0, true);
                    }
                    this.submitResult();
                }
            }, 300);
        },
        inputPasteHandler() {
            this.disableAutosearch = false
        },
        clickOutsideHandler() {
            setTimeout(() => {
                if(this.open && !this.standalone) {
                    this.closeResults();
                }
            }, 150);
        },
        shortcutArrowClickHandler(e, {sectionIndex, resultIndex}) {
            e.stopPropagation();
            this.disableAutosearch = true
            this.selectResult(sectionIndex, resultIndex, true);
            this.blurSubmitCheck = false;
            this.focusAfterAdress();
        },
        setWaypointOnIndexHandler(e) {
            if(e.waypointIndex === this.fieldIndex) {
                const result = e.result
                // format for last searches
                result.highlight = result.name || false
                result.comment = result.preparedPoiData && result.preparedPoiData.address ? result.preparedPoiData.address : false
                result.label = this.getDisplayName(result);
                this.resultSubmitted = true;
                this.storeSearchLocally(result);
                this.$emit('change', result);
            }
        },

        // Other methods
        addListeners() {
            eventBus.$on('setWaypointOnIndex', this.setWaypointOnIndexHandler)
            eventBus.$on('clearSearchFields', this.clearSearchFieldsHandler)
        },
        removeListeners() {
            eventBus.$off('setWaypointOnIndex', this.setWaypointOnIndexHandler)
            eventBus.$on('clearSearchFields', this.clearSearchFieldsHandler)
        },
        showDefaultState() {
            this.results = this.$set(this, 'results', this.defaultState);
            this.selectEmptyResult();
            this.defaultStateShown = true;
        },
        async loadSearchData() {
            this.loading = true;
            try {
                const response = await api.location.getLocation(this.searchText)
                this.loading = false;
                if(!response.data.data) {
                    this.clearResults();
                    return false;
                }
                if(this.defaultStateShown) {
                    this.deselectCurrentResult(); // deselect empty state result
                }
                let results = [
                    {
                        'items': response.data.data
                    }
                ]
                this.results = results;
                this.defaultStateShown = false;
                this.disableAutosearch = true
                this.selectResult(0,0);
            } catch(error) {
                if(!axios.isCancel(error)) {
                    this.loading = false;
                }
                console.error(error)
            }
        },
        selectNeighbourResult(dir) {
            if(dir === 'next') {
                if(this.results[this.selectedSectionIndex].items.length > this.selectedResultIndex + 1){ // go to next result inside this section
                    this.selectResult(this.selectedSectionIndex, this.selectedResultIndex + 1, true);
                } else if (this.results.length > this.selectedSectionIndex + 1) { // go to next section
                    this.selectResult(this.selectedSectionIndex + 1, 0, true);
                } else { // go to first result
                    this.selectResult(0, 0, true);
                }
            } else {
                if(this.selectedResultIndex > 0) { // go to previous result inside this section
                    this.selectResult(this.selectedSectionIndex, this.selectedResultIndex - 1, true);
                } else if (this.selectedSectionIndex > 0) { // go to last element of previous section
                    this.selectResult(this.selectedSectionIndex - 1, this.results[this.selectedSectionIndex - 1].items.length - 1, true);
                } else { // go to last element of last section
                    this.selectResult(this.results.length - 1, this.results[this.results.length - 1].items.length - 1, true);
                }
            }
        },
        selectResult(sectionIndex, resultIndex, updateTextInput = false) {
            if(this.results[this.selectedSectionIndex] && this.results[this.selectedSectionIndex].items[this.selectedResultIndex]) {
                this.results[this.selectedSectionIndex].items[this.selectedResultIndex].selected = false;
            }

            this.$set(this.results[sectionIndex].items[resultIndex], 'selected', true);
            // put selected name in input
            if(updateTextInput) this.searchText = this.getDisplayName(this.results[sectionIndex].items[resultIndex]);
            this.selectedSectionIndex = sectionIndex;
            this.selectedResultIndex = resultIndex;
            this.resultSubmitted = false
        },
        deselectCurrentResult() {
            if(this.selectedResultIndex === -1) return false;
            if(!this.results[this.selectedSectionIndex] || !this.results[this.selectedSectionIndex].items[this.selectedResultIndex]) return false;
            this.results[this.selectedSectionIndex].items[this.selectedResultIndex].selected = false;
            this.selectedSectionIndex = 0;
            this.selectedResultIndex = 0;
        },
        selectEmptyResult() {
            this.selectedSectionIndex = 0;
            this.selectedResultIndex = -1;
        },
        submitResult() {
            if(this.resultSubmitted) return false;
            let result;

            if(this.searchText.length && this.COORDINATE_REGEX.test(this.searchText.trim())) {
                const lat = this.searchText.split(',')[0].trim()
                const lon = this.searchText.split(',')[1].trim()
                result = {
                    label: this.searchText,
                    lat: lat,
                    lon: this.searchText.split(',')[1].trim(),
                    coordinatesResult: true,
                    name: 'Koordinaten',
                    comment: `${lat}, ${lon}`
                }
            } else if(this.selectedResultIndex >= 0) {
                result = this.results[this.selectedSectionIndex].items[this.selectedResultIndex];
                if(result.selected !== undefined) {
                    delete result.selected
                }
                if(result.link) {
                    this.linkClickHandler(result);
                    return false;
                }
                if(result.presetType) {
                    if(result.presetType === 'favourite') {
                        eventBus.$emit('showFavouritesPanel', {
                            specialPreset: result.id,
                            fieldIndex: this.fieldIndex
                        });
                        return false;
                    }   
                }
                result.label = this.getDisplayName(result);
                this.storeSearchLocally(result);
            } else {
                if(this.searchText.length) { // if no result is selected but there is text in the search field - it's a wrong address (not in GIS api)
                    result = {
                        label: this.searchText,
                        wrongAddress: true
                    };
                } else {
                    result = {}
                }
            }

            this.blurSubmitCheck = false;
            this.resultSubmitted = true;
            this.$emit('change', result);
            this.closeResults();
        },
        openResults() {
            if(this.open) return false;
            this.open = true;
            this.loadDropdownContent()
        },
        closeResults() {
            this.open = false;
            this.clearResults();
        },
        clearResults() {
            this.selectEmptyResult();
            this.results = [
                {
                    'items': []
                }
            ];
        },
        loadDropdownContent() {
            if(this.searchText && this.searchText.length) {
                if(!this.resultSubmitted) {
                    if(!this.searchText || this.searchText.length < 3) return false; // don't load suggestion for short strings
                    if(!this.open) return false; // don't load suggestions if the dropdown is closed

                    clearTimeout(this.loadSearchDataTimer)

                    this.loadSearchDataTimer = setTimeout(() => {
                        if (!this.resultSubmitted) this.loadSearchData()
                    }, 500)
                }
            } else {
                this.showDefaultState()
            }
        },
        focusOnInput() {
            this.$refs['search-field'].focus();
        },
        selectInput() {
            this.$refs['search-field'].select();
        },
        storeSearchLocally(result) {
            this.$store.dispatch('storeSearchLocationLocally', result);
        },
        getDisplayName(result) {      
            if(result.link) return '';
            if(result.present && result.content) return result.label;
            else if(result.preset && !result.content) return result.comment;
            if(result.preparedPoiData && result.preparedPoiData.type === 'favourites') {
                return `${result.name}`
            }
            return `${result.name || result.highlight.replace('<b>', '').replace('</b>', '')}${result.comment ? `, ${result.comment}` : ''}`;
        },
        getIconForLocationCategory(type) {
            let icon = false;
            const icons = {
                'GEO_CENTER': 'Location',
                'GEO_STREET': 'Street',
                'GEO_REGION': 'Location',
                'ADRESSE': 'Pin',
                'POI_AIRPORT': 'Plane',
                'POI_BORDER': 'Border',
                'POI_BUSSTATION': 'Bus',
                'POI_GENERAL': 'PinBig',
                'POI_OEAMTCBASE': 'OEAMTC',
                'POI_PARKING': 'Parking',
                'POI_PASSROAD': 'MountainRoad',
                'POI_ROADHOUSE': 'Reststop',
                'POI_SHIP': 'Ship',
                'POI_TRAINSTATION': 'Train',
                'POI_TUNNEL': 'Tunnel',
                'HOME': 'House',
                'WORK': 'Suitcase',
                'FAVOURITES': 'StarOutlineYellow',
                'LOCATION': 'LocateOutline',
            }
            if(icons[type]){
                icon = 'Icon' + icons[type];
            }
            return icon;
        },
        getBiggerIcon(type) {
            if(type === 'FAVOURITES' || type ==='HOME' || type === 'WORK' || type === 'LOCATION') return true
            return false;
        },
        getImageForLocationCategory(type) {
            let image = false;
            const images = {
                'ROUTE': `${process.env.BASE_URL}/images/ui/route-icon.svg`,
            }
            if(images[type]){
                image = images[type];
            }
            return image;
        },
        focusAfterAdress() {
            this.$refs['search-field'].focus();
            setTimeout(() => {
                const commaIndex = this.searchText.indexOf(',');
                if(commaIndex >= 0) {
                    this.$refs['search-field'].setSelectionRange(commaIndex, commaIndex);
                }
            }, 0);
        },
        getLastRoutesItems() {
            let items = [];
            this.lastRoutes.forEach((route, index) => {
                const firstWaypoint = route[0];
                const lastWaypoint = route[route.length - 1];
                let item = {
                    type: 'ROUTE',
                    highlight: lastWaypoint.name,
                    name: lastWaypoint.name,
                    comment: `Von ${firstWaypoint.name}`,
                    link: 'last-route',
                    visualType: 'image',
                    lastRoutesIndex: index,
                    id: `route-${index}`,
                }
                items.push(item);
            })
            return items;
        },
        linkClickHandler(result) {
            switch(result.link) {
                case 'last-route':
                    eventBus.$emit('loadLastSearchedRoute', result.lastRoutesIndex);
                    this.$emit('close');
                    this.closeResults();
                break;
                case 'favourite-list':
                    if(!this.user) {
                        eventBus.$emit('showLoginMenu')
                    } else {
                        if(this.mobile) {
                            eventBus.$emit('showMobileFavouritePicker')
                        } else {
                            this.$store.commit('setFavouritePickerOpen', true)
                            this.$store.commit('setFavouritePickerFieldIndex', this.fieldIndex)
                        }
                    }
                break;  
            }
        },
        autoSetResult(result) {
            if(result && result.name) {
                this.results = [
                    {
                        items: [result]
                    }
                ]
                this.selectResult(0, 0, true)
                if(this.mapFullyLoaded) {
                    setTimeout(() => {
                        this.submitResult() 
                    }, 10);
                } else {
                    this.autoSubmitResult = true
                }
            } else {
                this.searchText = '';
                this.resultSubmitted = false;
                this.selectEmptyResult();
                this.submitResult();
            }
        },
        debouncedCoordinateSearch: debounce(function() {
            if(this.COORDINATE_REGEX.test(this.searchText.trim())) this.submitResult()
        }, 1000),
        clearField() {
            clearTimeout(this.loadSearchDataTimer)

            this.searchText = '';
            this.selectEmptyResult();
            this.submitResult();
            this.disableAutosearch = false
            this.resultSubmitted = false;
        }
    }
}
</script>

<style lang="scss">
.search {
    $block: &;
    position: relative;
    &.is-results-open {
        z-index: 10;
    }
    &__field {
        background-color: rgba(#000, 0.05);
        background-color: $color-black-005-solid;
        border-radius: 8px;
        display: flex;
        align-items: center;
        padding: 5px 12px;
        position: relative;
        .is-side-space &{
            margin-right: 40px;
        }
        .is-mobile-disabled & {
            pointer-events: none;
        }
        .is-error & {
            border: 1px solid red; // todo: exact color 
        }
        &.is-disabled {
            input {
                opacity: 0.4;
                color: currentColor;
            }
            #{$block} {
                &__field{
                    &__icon {
                        opacity: 0.4;
                    }
                }
            }
        }
        @media (hover: hover) {
            &:hover {
                #{$block} {
                    &__field{
                        &__drag-handle {
                            display: block;
                        }
                    }
                }
            }
        }
        &__icon {
            opacity: 0.4;
            flex-shrink: 0;
            line-height: 1;
            .is-error & {
                color: red; // todo: exact color 
            }
            svg {
                display: block;
            }
            .is-error &,
            .is-filled & {
                opacity: 1;
            }
        }
        &__number {
            width: 24px;
            text-align: center;
            font-size: 15px;
            color: $color-grey-dark;
            font-weight: $font-weight-bold;
        }
        &__input {
            border: 0;
            outline: 0;
            padding: 2px 0 2px 12px;
            background: none;
            font-size: 16px;
            flex-grow: 1;
            @include mq(tablet) {
                font-size: 15px;
            }
            &::placeholder {
                opacity: 0.4;
                color: currentColor;
            }
        }
        &__actions {
            display: flex;
            align-items: center;
            position: relative;
             &::before {
                content: '';
                position: absolute;
                right: 100%;
                top: 0;
                bottom: 0;
                width: 20px;
                pointer-events: none;
                background: linear-gradient(-90deg, $color-black-005-solid 20%, rgba($color-black-005-solid, 0) 100%);
            }
        }
        &__clear {
            display: none;
            flex-shrink: 0;
            color: $color-grey;
            cursor: pointer;
            .is-filled.is-results-open & {
                display: block;
            }
            .no-touch &:hover {
                color: $color-black;
            }
            svg {
                display: block;
            }
        }
        &__drag-handle {
            display: block;
            line-height: 1;
            color: $color-grey;
            margin-bottom: -2px;
            margin-left: 2px;
            cursor: move;
            @include mq(desktop) {
                display: none;
            }
            .is-filled.is-results-open & {
                display: none;
            }   
            .sortable-chosen &,
            &:hover {
                color: $color-black;
                cursor: move;
            }
            .is-in-panel & {
                display: none !important;
            }
        }
    }
    &__results {
        position: absolute;
        top: 100%;
        margin-top: 8px;
        left: -$sidebar-horizontal-padding;
        right: -$sidebar-horizontal-padding;
        background-color: white;
        border-radius: 8px;
        box-shadow: 0 2px 2px 0 rgba(#000, 0.35);
        display: none;
        overflow: hidden;
        z-index: 1;
        .is-results-open & {
            display: block;
        }
        .is-standalone & {
            display: block !important;
            box-shadow: none;
            border-radius: none;
        }
        .is-in-panel & {
            border-top-right-radius: 0;
            border-top-left-radius: 0;
            margin-top: 6px;
            @include mq(tablet) {
                max-height: 43px * 6;
                overflow: auto;
                box-shadow: 0 2px 2px 0 rgba(#000, 0.35);
                left: -30px;
                right: -30px;
            }
        }
        &__section{
            margin-bottom: 20px;
            &:last-child {
                margin-bottom: 0;
                #{$block}__result {
                    &:last-child {
                        #{$block}__result__inner { 
                            border-bottom: none;
                        }
                    }
                }
            }
            &__label {
                font-size: 12px;
                opacity: 0.5;
                padding: 0 $sidebar-horizontal-padding + 12px; 
                margin-top: 8px;
            }
        }
    }
    &__result {
        padding: 0 $sidebar-horizontal-padding + 12px; 
        cursor: pointer;
        &--named {
            #{$block}__result {
                &__inner {
                    // padding: 5px 0;
                    padding: 6px 0;

                    &--spacing-big {
                        padding: 15px 0;

                        @include mq(tablet) {
                            padding: 11.5px 0;
                        }
                    }
                }
                &__texts {
                    overflow: hidden;
                    position: relative;
                    &::before {
                        content: '';
                        position: absolute;
                        right: 0;
                        top: 0;
                        bottom: 0;
                        width: 20px;
                        pointer-events: none;
                        background: linear-gradient(-90deg, #fff 20%, rgba(#fff, 0) 100%);
                        z-index: 1;
                        // background: linear-gradient(-90deg, $color-black-005-solid 20%, rgba($color-black-005-solid, 0) 100%);
                    }
                    &__name {
                        display: block;
                        margin-right: 0;
                    }
                    &__address {
                        white-space: nowrap;
                    }
                }
            }
        }
        &--spacious {
            #{$block}__result {
                &__inner {
                    padding: 14px 0;
                }
            }
        }
        &--selected {
            @include mq(tablet) {
                background-color: $color-black-005;
            }
            #{$block}__result__texts {
                &::before {
                    background: linear-gradient(-90deg, $color-black-005-solid 20%, rgba($color-black-005-solid, 0) 100%);
                }
            }
        }
        &--bold-name {
            #{$block}__result {
                &__texts {
                    &__name {
                        font-weight: $font-weight-medium;
                    }
                }
            }
        }
        &__inner {
            padding: 9px 0;
            display: flex;
            align-items: center;
            border-bottom: 1px solid rgba(#000, 0.05);
        }
        &__image {
            width: 24px;
            height: 24px;
            margin-right: 12px;
            margin-top: 5px;
            margin-bottom: 5px;

            &--big {
                width: 40px;
                height: 40px;
            }
        }
        &__icon {
            color: $color-grey-dark;
            margin-right: 12px;
            flex-shrink: 0;
        }
        &__texts {
            flex-grow: 1;
            &__name {
                margin-right: 5px;
                b {
                    font-weight: $font-weight-medium;
                }
            }
            &__address {
                font-size: 15px;
                opacity: 0.5;
            }
        }
        &__arrow {
            line-height: 1;
            color: $color-grey;
            padding: 8px 0 8px 12px;
            svg {
                display: block;
            }
        }
        &__link-icon {
            opacity: 0.4;

            &--close {
                display: flex;
                align-items: center;
            }
        }
    }
}
</style>